import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiService, Match, Team } from '../services/api';
import StandingsMatrix from '../components/StandingsMatrix';
import { buildStandings } from '../utils/standings';

const SummerLeague: React.FC = () => {
  const { year, group } = useParams<{ year: string; group: string }>();
  const [teams, setTeams] = useState<Team[]>([]);
  const [matches, setMatches] = useState<Match[]>([]);

  useEffect(() => {
    if (!year || !group) return;

    const load = async () => {
      try {
        const yearNum = parseInt(year, 10);
        const [teamsData, matchesData] = await Promise.all([
          apiService.getTeams(),
          apiService.getMatches(),
        ]);
        setTeams(
          teamsData.filter(
            (team) => team.year === yearNum && team.group === group
          )
        );
        setMatches(
          matchesData.filter(
            (match) => match.year === yearNum && match.group === group
          )
        );
      } catch (error) {
        console.error('Failed to load standings data:', error);
        setTeams([]);
        setMatches([]);
      }
    };

    load();
  }, [year, group]);

  const standings = useMemo(() => {
    const groupTeams = teams.map((team) => ({ id: team.id, name: team.name }));
    const groupMatches = matches
      .filter(
        (match) =>
          match.prelim === true &&
          match.team1?.id &&
          match.team2?.id &&
          match.score1 != null &&
          match.score2 != null
      )
      .map((match) => ({
        team1Id: match.team1.id,
        team2Id: match.team2.id,
        score1: match.score1 as number,
        score2: match.score2 as number,
      }));
    return buildStandings(groupTeams, groupMatches);
  }, [teams, matches]);

  if (!year || !group) {
    return (
      <div style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        padding: '20px',
        textAlign: 'center'
      }}>
        <div>Invalid URL parameters</div>
      </div>
    );
  }

  return (
    <div style={{ 
      maxWidth: '1200px',
      width: '100%',
      margin: '0 auto', 
      padding: '0 20px 20px 20px',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#e3f2fd',
      boxSizing: 'border-box',
    }}>
      {/* Navigation */}
      <div style={{ marginBottom: '40px', paddingTop: '20px' }}>
        <Link 
          to={`/summer-league/${year}`}
          style={{ 
            color: '#0066cc', 
            textDecoration: 'none', 
            fontSize: '16px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          ← 返回
        </Link>
      </div>

      {/* Header */}
      <div style={{ 
        textAlign: 'center',
        marginBottom: '60px'
      }}>
        <h1 style={{ 
          fontSize: '3rem', 
          color: '#2d3748',
          marginBottom: '12px',
          fontWeight: '700'
        }}>
          Summer League {year}
        </h1>
        <h2 style={{ 
          fontSize: '2rem', 
          color: '#4a5568',
          marginBottom: '8px',
          fontWeight: '500'
        }}>
          Group {group}
        </h2>
        <div style={{
          width: '60px',
          height: '4px',
          backgroundColor: '#3182ce',
          margin: '0 auto',
          borderRadius: '2px'
        }}></div>
      </div>

      {/* Navigation Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
        gap: '30px',
        marginBottom: '40px'
      }}>
        {/* Schedule Card */}
        <Link 
          to={`/summer-league/${year}/${group}/schedule`}
          style={{
            textDecoration: 'none',
            color: 'inherit',
            display: 'block'
          }}
        >
          <div style={{
            backgroundColor: '#e3f2fd',
            borderRadius: '16px',
            padding: '40px 30px',
            textAlign: 'center',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
            border: '2px solid transparent',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden',
            minWidth: '200px',
            flexShrink: 0
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.15)';
            e.currentTarget.style.borderColor = '#3182ce';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.1)';
            e.currentTarget.style.borderColor = 'transparent';
          }}
          >
            {/* Icon */}
            <div style={{
              width: '80px',
              height: '80px',
              backgroundColor: '#3182ce',
              borderRadius: '50%',
              margin: '0 auto 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '32px',
              color: 'white'
            }}>
              📅
            </div>
            
            {/* Title */}
            <h3 style={{
              fontSize: '1.8rem',
              color: '#2d3748',
              marginBottom: '12px',
              fontWeight: '600'
            }}>
              賽程
            </h3>
            
            {/* Description */}
            <p style={{
              fontSize: '1rem',
              color: '#718096',
              marginBottom: '20px',
              lineHeight: '1.5'
            }}>
              查看比賽時間表和結果
            </p>
            
            {/* Arrow */}
            <div style={{
              fontSize: '1.2rem',
              color: '#3182ce',
              fontWeight: 'bold'
            }}>
              →
            </div>
          </div>
        </Link>

        {/* Teams Card */}
        <Link 
          to={`/summer-league/${year}/${group}/teams`}
          style={{
            textDecoration: 'none',
            color: 'inherit',
            display: 'block'
          }}
        >
          <div style={{
            backgroundColor: '#e3f2fd',
            borderRadius: '16px',
            padding: '40px 30px',
            textAlign: 'center',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
            border: '2px solid transparent',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden',
            minWidth: '200px',
            flexShrink: 0
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.15)';
            e.currentTarget.style.borderColor = '#3182ce';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.1)';
            e.currentTarget.style.borderColor = 'transparent';
          }}
          >
            {/* Icon */}
            <div style={{
              width: '80px',
              height: '80px',
              backgroundColor: '#3182ce',
              borderRadius: '50%',
              margin: '0 auto 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '32px',
              color: 'white'
            }}>
              🏀
            </div>
            
            {/* Title */}
            <h3 style={{
              fontSize: '1.8rem',
              color: '#2d3748',
              marginBottom: '12px',
              fontWeight: '600'
            }}>
              球隊介紹
            </h3>
            
            {/* Description */}
            <p style={{
              fontSize: '1rem',
              color: '#718096',
              marginBottom: '20px',
              lineHeight: '1.5'
            }}>
              瀏覽參賽球隊和球員資料
            </p>
            
            {/* Arrow */}
            <div style={{
              fontSize: '1.2rem',
              color: '#3182ce',
              fontWeight: 'bold'
            }}>
              →
            </div>
          </div>
        </Link>

        {/* Awards Card */}
        <Link 
          to={`/summer-league/${year}/${group}/awards`}
          style={{
            textDecoration: 'none',
            color: 'inherit',
            display: 'block'
          }}
        >
          <div style={{
            backgroundColor: '#e3f2fd',
            borderRadius: '16px',
            padding: '40px 30px',
            textAlign: 'center',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
            border: '2px solid transparent',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden',
            minWidth: '200px',
            flexShrink: 0
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.15)';
            e.currentTarget.style.borderColor = '#3182ce';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.1)';
            e.currentTarget.style.borderColor = 'transparent';
          }}
          >
            {/* Icon */}
            <div style={{
              width: '80px',
              height: '80px',
              backgroundColor: '#3182ce',
              borderRadius: '50%',
              margin: '0 auto 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '32px',
              color: 'white'
            }}>
              🏆
            </div>
            
            {/* Title */}
            <h3 style={{
              fontSize: '1.8rem',
              color: '#2d3748',
              marginBottom: '12px',
              fontWeight: '600'
            }}>
              獎項
            </h3>
            
            {/* Description */}
            <p style={{
              fontSize: '1rem',
              color: '#718096',
              marginBottom: '20px',
              lineHeight: '1.5'
            }}>
              查看團體與個人獎項
            </p>
            
            {/* Arrow */}
            <div style={{
              fontSize: '1.2rem',
              color: '#3182ce',
              fontWeight: 'bold'
            }}>
              →
            </div>
          </div>
        </Link>
      </div>

      <section style={{ marginBottom: 40, width: '100%', maxWidth: '100%' }}>
        <StandingsMatrix columns={standings.columns} rows={standings.rows} />
      </section>

      {/* Footer Info */}
      <div style={{
        textAlign: 'center',
        color: '#a0aec0',
        fontSize: '0.9rem'
      }}>
        <p>選擇上方選項開始瀏覽</p>
      </div>
    </div>
  );
};

export default SummerLeague; 